import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Phone, Mail, MapPin, Clock, Instagram, Facebook } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_dining-guide-3/artifacts/egfzykn3_image.png";

const Footer = () => {
  const { t, language } = useLanguage();

  return (
    <footer 
      className="relative bg-gradient-to-b from-hanse-navy to-hanse-dark overflow-hidden"
      data-testid="footer"
    >
      {/* Decorative Background */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-hanse-red rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-amber-500 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 container mx-auto px-4 pt-16 pb-8">
        {/* Premium Logo Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-col items-center mb-12"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-hanse-red to-amber-500 rounded-full blur-xl opacity-30"></div>
            <img
              src={LOGO_URL}
              alt="Hanse Pizza Logo"
              className="relative w-24 h-24 rounded-full border-3 border-amber-400/50 shadow-2xl"
            />
          </div>
          <h3 className="font-heading text-2xl font-bold mt-4">
            <span className="text-hanse-red">HANSE</span>{' '}
            <span className="text-white">PIZZA</span>
          </h3>
          <p className="text-white/50 text-sm mt-1">Bremen, Germany</p>
        </motion.div>

        {/* Four Columns - Premium Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          
          {/* Opening Hours */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10"
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-400" />
              </span>
              <h4 className="font-heading text-lg font-bold text-white">
                {t('contact.hours')}
              </h4>
            </div>
            <div className="space-y-2 text-white/70 text-sm">
              <p>{language === 'de' ? 'Montag - Sonntag' : 'Monday - Sunday'}</p>
              <p className="text-amber-400 font-semibold text-lg">11:00 - 22:00</p>
            </div>
          </motion.div>

          {/* Contact - Combined Phone, Email & Address */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10"
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                <Phone className="w-5 h-5 text-amber-400" />
              </span>
              <h4 className="font-heading text-lg font-bold text-white">
                {language === 'de' ? 'Kontakt' : 'Contact'}
              </h4>
            </div>
            <div className="space-y-3 text-sm">
              <a href="tel:+4942148535145" className="flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-colors font-semibold">
                <Phone className="w-4 h-4" />
                +49 421 48535145
              </a>
              <a href="mailto:Info@hansepizzabremen.de" className="flex items-center gap-2 text-white/70 hover:text-amber-400 transition-colors">
                <Mail className="w-4 h-4" />
                Info@hansepizzabremen.de
              </a>
              <div className="flex items-start gap-2 text-white/60 pt-2 border-t border-white/10 mt-2">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Industriestraße 12-12A, 28199 Bremen</span>
              </div>
            </div>
          </motion.div>

          {/* Follow Us */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10"
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                <Instagram className="w-5 h-5 text-amber-400" />
              </span>
              <h4 className="font-heading text-lg font-bold text-white">
                {t('contact.followUs')}
              </h4>
            </div>
            <div className="flex flex-wrap gap-3">
              <a 
                href="https://www.instagram.com/hansepizza?igsh=ZnZvejlqdnczdjBk&utm_source=qr" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full text-white/80 hover:text-amber-400 transition-all border border-white/20 hover:border-amber-400/50"
              >
                <Instagram className="w-4 h-4" />
                <span className="text-sm">Instagram</span>
              </a>
              <a 
                href="https://www.facebook.com/share/17iyAZZSBE/?mibextid=wwXIfr" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full text-white/80 hover:text-amber-400 transition-all border border-white/20 hover:border-amber-400/50"
              >
                <Facebook className="w-4 h-4" />
                <span className="text-sm">Facebook</span>
              </a>
            </div>
          </motion.div>
        </div>

        {/* Divider */}
        <div className="border-t border-white/10 pt-8">
          {/* Bottom Bar */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/40 text-sm">
              © 2026 Hanse Pizza Bremen. {t('footer.rights')}
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <a href="#" className="text-white/40 hover:text-amber-400 transition-colors px-2">
                {t('footer.impressum')}
              </a>
              <span className="text-white/20">|</span>
              <a href="#" className="text-white/40 hover:text-amber-400 transition-colors px-2">
                {t('footer.datenschutz')}
              </a>
              <span className="text-white/20">|</span>
              <a href="#" className="text-white/40 hover:text-amber-400 transition-colors px-2">
                {t('footer.allergens')}
              </a>
            </div>
          </div>

          {/* Closing Line - Premium */}
          <motion.p 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center text-white/30 text-sm mt-8 flex items-center justify-center gap-2"
          >
            <span className="h-px w-8 bg-gradient-to-r from-transparent to-white/20"></span>
            {t('footer.closing')}
            <span className="h-px w-8 bg-gradient-to-l from-transparent to-white/20"></span>
          </motion.p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
